import { Link } from "react-router-dom";
import type { Article } from "@/lib/articles";
import { formatDate, formatViews, getCategoryById, getSubCategoryName } from "@/lib/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "grid" | "list";
}

export default function ArticleCard({ article, variant = "grid" }: ArticleCardProps) {
  const category = getCategoryById(article.categoryId);

  if (variant === "list") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 md:p-4 transition-colors hover:border-primary-300 cursor-pointer"
      >
        <div className="h-24 w-32 md:h-28 md:w-44 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
              {category?.name}
            </span>
            <span>{getSubCategoryName(article.categoryId, article.subcategory)}</span>
          </div>
          <h3 className="mt-2 line-clamp-2 font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="mt-1.5 line-clamp-2 text-xs md:text-sm text-foreground-600">
            {article.summary}
          </p>
          <div className="mt-2 hidden sm:flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span>{formatDate(article.publishedAt)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 cursor-pointer"
    >
      <div className="h-44 w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-4">
        <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
            {category?.name}
          </span>
          <span>{getSubCategoryName(article.categoryId, article.subcategory)}</span>
        </div>
        <h3 className="mt-2.5 line-clamp-2 font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-foreground-600">{article.summary}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span>{article.author}</span>
          <span className="flex items-center gap-3">
            <span>{formatDate(article.publishedAt)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
              {formatViews(article.views)}
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}